import type {
  DataModelItem,
  KnowledgeBaseItem,
  OrchestrationItem,
  ReportCatalogItem,
} from "./intentDefinitionService";

export type IntentClassification = "requesting_action" | "requesting_report" | "live_report" | "general_search";
export type ReportDeliveryMode = "live" | "analytics_hub";

export interface ParameterSlot {
  id: string;
  name: string;
  parameterType?: "claim" | "parameter";
  claim?: string;
  dataType?: string;
  values?: string[];
  required: boolean;
}

export interface SelectedOrchestrationMapping {
  orchestrationId: string;
  keywords: string[];
  parameters: ParameterSlot[];
}

export interface SelectedReportMapping {
  reportId: string;
  keywords: string[];
  parameters: ParameterSlot[];
}

export interface IntentParameterPayload {
  name: string;
  parameter_type?: "claim" | "parameter";
  claim?: string;
  data_type?: string;
  values?: string[];
  required: boolean;
}

export interface IntentOrchestratorPayload {
  id: string;
  name: string;
  keywords: string[];
  parameters: IntentParameterPayload[];
}

export interface IntentDataModelPayload {
  id: string;
  name: string;
  additional_context_instructions: string;
  /** Backend may return an array or a single parameter object. */
  parameters?: IntentParameterPayload | IntentParameterPayload[] | null;
  keywords?: string | string[] | null;
}

export interface IntentKnowledgeBasePayload {
  id: string;
  name: string;
}

export interface IntentClassificationPayload {
  intent_type: string;
  orchestrators?: IntentOrchestratorPayload[];
  clarification_question?: string;
  parameter_prompt?: string;
  report_type?: string;
  data_models?: IntentDataModelPayload[];
  /** Live-report context; also mirrored onto data_models[0]. */
  additional_context_instructions?: string;
  parameters?: string;
  keywords?: string | string[];
  knowledge_bases?: IntentKnowledgeBasePayload[];
}

export interface IntentFallbackPayload {
  confirm_status_for_low_confidence: boolean;
  low_confidence_value: number;
  confirm_when_multiple_routes_match: boolean;
  confirm_when_parameters_missing: boolean;
  fallback_message: string;
}

export interface IntentDefinitionPayload {
  _id?: string;
  chatbot_id: string;
  name: string;
  description: string;
  code: string;
  status: string;
  confidence_threshold: number;
  intent_classification: IntentClassificationPayload;
  training_phrases: string[];
  fallback: IntentFallbackPayload;
}

export interface IntentDefinitionRecord extends IntentDefinitionPayload {
  _id: string;
  tenant_id?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: Record<string, unknown>;
  updated_by?: Record<string, unknown>;
}

/** Result of loading a full intent for edit mode */
export interface ParsedIntentLoadResult {
  snapshot: Partial<IntentFormSnapshot>;
  orchestrationItems: OrchestrationItem[];
  reportItems: ReportCatalogItem[];
  knowledgeBaseItems: KnowledgeBaseItem[];
  dataModelItems: DataModelItem[];
}

/** Summary row from GET /api/intent-definitions?chatbot_id= */
export interface IntentDefinitionListItem {
  _id: string;
  tenant_id?: string;
  chatbot_id: string;
  name: string;
  code: string;
  status: string;
  confidence_threshold: number;
  updated_at?: string;
}

export interface IntentFormSnapshot {
  intentId?: string;
  chatbotId: string;
  intentName: string;
  intentCode: string;
  description: string;
  isActive: boolean;
  confidenceThreshold: number;
  classification: IntentClassification;
  selectedOrchestrationMappings: SelectedOrchestrationMapping[];
  orchestrations: OrchestrationItem[];
  reportDeliveryMode: ReportDeliveryMode;
  askClarifyingQuestion: boolean;
  clarifyingQuestion: string;
  parameterPrompt: string;
  selectedDataModel: string;
  liveReportInstructions: string;
  liveDataModelMapping: { keywords: string[]; parameters: ParameterSlot[] };
  dataModels: DataModelItem[];
  selectedReportMappings: SelectedReportMapping[];
  reportCatalog: ReportCatalogItem[];
  selectedKnowledgeBases: string[];
  knowledgeBases: KnowledgeBaseItem[];
  generalSearchKeywords: string[];
  trainingPhrases: string[];
  confirmBelowThreshold: boolean;
  confirmationThreshold: number;
  confirmMultipleRoutes: boolean;
  confirmMissingParams: boolean;
  confirmationMessage: string;
}

const INTENT_TYPE_TO_API: Record<IntentClassification, string> = {
  requesting_action: "action",
  requesting_report: "requesting report",
  live_report: "requesting report",
  general_search: "general search",
};

const INTENT_TYPE_FROM_API: Record<string, IntentClassification> = {
  action: "requesting_action",
  "requesting action": "requesting_action",
  requesting_action: "requesting_action",
  "requesting report": "requesting_report",
  requesting_report: "requesting_report",
  "live report": "live_report",
  live_report: "live_report",
  "general search": "general_search",
  general_search: "general_search",
};

export function normalizeKeywords(keywords: string[]): string[] {
  return keywords.map((keyword) => keyword.trim()).filter(Boolean);
}

export function serializeKeywords(keywords: string[]): string {
  return normalizeKeywords(keywords).join(", ");
}

function appendUniqueKeyword(keywords: string[], pending: string): string[] {
  const trimmed = pending.trim();
  if (!trimmed) return keywords;
  if (keywords.some((keyword) => keyword.toLowerCase() === trimmed.toLowerCase())) return keywords;
  return [...keywords, trimmed];
}

export function mergePendingReportKeywordDrafts(
  mappings: SelectedReportMapping[],
  drafts: Record<string, string>
): SelectedReportMapping[] {
  return mappings.map((mapping) => {
    const pending = drafts[mapping.reportId];
    if (!pending?.trim()) return mapping;
    return { ...mapping, keywords: appendUniqueKeyword(mapping.keywords, pending) };
  });
}

export function mergePendingOrchestrationKeywordDrafts(
  mappings: SelectedOrchestrationMapping[],
  drafts: Record<string, string>
): SelectedOrchestrationMapping[] {
  return mappings.map((mapping) => {
    const pending = drafts[mapping.orchestrationId];
    if (!pending?.trim()) return mapping;
    return { ...mapping, keywords: appendUniqueKeyword(mapping.keywords, pending) };
  });
}

export function mergePendingGeneralSearchKeywordDraft(
  keywords: string[],
  draft: string
): string[] {
  return appendUniqueKeyword(keywords, draft);
}

export function mergePendingLiveDataModelKeywordDraft(
  mapping: { keywords: string[]; parameters: ParameterSlot[] },
  draft: string
): { keywords: string[]; parameters: ParameterSlot[] } {
  if (!draft.trim()) return mapping;
  return { ...mapping, keywords: appendUniqueKeyword(mapping.keywords, draft) };
}

export interface ParameterDraft {
  name: string;
  parameterType?: "claim" | "parameter";
  claim?: string;
  dataType?: string;
  values?: string[];
  required: boolean;
}

function appendUniqueParameter(parameters: ParameterSlot[], draft: ParameterDraft): ParameterSlot[] {
  const trimmed = draft.name.trim();
  if (!trimmed) return parameters;
  if (parameters.some((param) => param.name === trimmed)) return parameters;
  const claim = draft.claim?.trim();
  const parameterType = draft.parameterType ?? (claim ? "claim" : undefined);
  const dataType = draft.dataType?.trim();
  const values = Array.isArray(draft.values)
    ? draft.values.map((value) => value.trim()).filter(Boolean)
    : [];
  return [
    ...parameters,
    {
      id: `p-${Date.now()}`,
      name: trimmed,
      ...(parameterType ? { parameterType } : {}),
      ...(claim ? { claim } : {}),
      ...(dataType ? { dataType } : {}),
      ...(dataType === "array" && values.length > 0 ? { values } : {}),
      required: Boolean(draft.required),
    },
  ];
}

export function mergePendingOrchestrationParameterDrafts(
  mappings: SelectedOrchestrationMapping[],
  drafts: Record<string, ParameterDraft>
): SelectedOrchestrationMapping[] {
  return mappings.map((mapping) => {
    const pending = drafts[mapping.orchestrationId];
    if (!pending?.name?.trim()) return mapping;
    return { ...mapping, parameters: appendUniqueParameter(mapping.parameters, pending) };
  });
}

export function mergePendingReportParameterDrafts(
  mappings: SelectedReportMapping[],
  drafts: Record<string, ParameterDraft>
): SelectedReportMapping[] {
  return mappings.map((mapping) => {
    const pending = drafts[mapping.reportId];
    if (!pending?.name?.trim()) return mapping;
    return { ...mapping, parameters: appendUniqueParameter(mapping.parameters, pending) };
  });
}

export function mergePendingLiveDataModelParameterDraft(
  mapping: { keywords: string[]; parameters: ParameterSlot[] },
  draft: ParameterDraft
): { keywords: string[]; parameters: ParameterSlot[] } {
  if (!draft.name.trim()) return mapping;
  return { ...mapping, parameters: appendUniqueParameter(mapping.parameters, draft) };
}

export function parseKeywords(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(String).map((item) => item.trim()).filter(Boolean);
  }
  if (typeof value === "string" && value.trim()) {
    return value.split(",").map((item) => item.trim()).filter(Boolean);
  }
  return [];
}

export function mapStartContractFieldsToParameters(
  fields: Array<{
    id?: string;
    key: string;
    label?: string;
    dataType?: string;
    required?: boolean;
    options?: string[];
  }>
): ParameterSlot[] {
  const parameters: ParameterSlot[] = [];
  fields.forEach((field, index) => {
    const name = String(field.key ?? "").trim();
    if (!name) return;
    const dataType = String(field.dataType ?? "text").trim() || "text";
    const values = Array.isArray(field.options)
      ? field.options.map((option) => String(option).trim()).filter(Boolean)
      : [];
    parameters.push({
      id: String(field.id ?? "").trim() || `wf-field-${index}`,
      name,
      parameterType: "parameter",
      dataType,
      ...(values.length > 0 ? { values } : {}),
      required: Boolean(field.required),
    });
  });
  return parameters;
}

export function normalizeParameters(parameters: ParameterSlot[]): IntentParameterPayload[] {
  return parameters
    .map((param) => {
      const claim = param.claim?.trim();
      const parameterType = param.parameterType ?? (claim ? "claim" : undefined);
      const values = Array.isArray(param.values)
        ? param.values.map((value) => value.trim()).filter(Boolean)
        : [];
      return {
        name: param.name.trim(),
        ...(parameterType ? { parameter_type: parameterType } : {}),
        ...(claim ? { claim } : {}),
        ...(parameterType === "parameter" && param.dataType?.trim()
          ? { data_type: param.dataType.trim() }
          : {}),
        ...(parameterType === "parameter" && values.length > 0 ? { values } : {}),
        required: parameterType === "claim" ? false : Boolean(param.required),
      };
    })
    .filter((param) => param.name);
}

/** @deprecated Prefer normalizeParameters — API expects a JSON array, not a stringified blob. */
export function serializeParameters(parameters: ParameterSlot[]): string {
  return JSON.stringify(normalizeParameters(parameters));
}

function mapParameterItem(item: unknown, index: number): ParameterSlot | null {
  if (typeof item === "string") {
    const name = item.trim();
    return name ? { id: `p-${index}-${Date.now()}`, name, required: false } : null;
  }

  if (!item || typeof item !== "object") return null;

  const record = item as Record<string, unknown>;
  const name = String(record.name ?? "").trim();
  if (!name) return null;

  const claim =
    record.claim != null && String(record.claim).trim()
      ? String(record.claim).trim()
      : undefined;
  const rawParameterType =
    record.parameter_type ?? record.parameterType ?? record.source_type ?? record.sourceType;
  const parameterType =
    rawParameterType === "claim" || rawParameterType === "parameter"
      ? rawParameterType
      : claim
        ? "claim"
        : "parameter";
  const dataTypeValue = record.data_type ?? record.dataType;
  const dataType =
    dataTypeValue != null && String(dataTypeValue).trim()
      ? String(dataTypeValue).trim()
      : parameterType === "parameter"
        ? "string"
        : undefined;
  const rawValues = record.values ?? record.Values ?? record.options ?? record.Options;
  const values = Array.isArray(rawValues)
    ? rawValues.map((value) => String(value).trim()).filter(Boolean)
    : [];

  return {
    id: `p-${index}-${Date.now()}`,
    name,
    parameterType,
    ...(claim ? { claim } : {}),
    ...(dataType ? { dataType } : {}),
    ...(values.length > 0 ? { values } : {}),
    required: Boolean(record.required),
  };
}

export function parseParameters(value: unknown): ParameterSlot[] {
  // Backend may return a single parameter object instead of an array.
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const single = mapParameterItem(value, 0);
    return single ? [single] : [];
  }

  if (Array.isArray(value)) {
    return value
      .map((item, index) => mapParameterItem(item, index))
      .filter((item): item is ParameterSlot => Boolean(item));
  }

  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return parseParameters(parsed);
    } catch {
      return value
        .split(",")
        .map((name, index) => mapParameterItem(name, index))
        .filter((item): item is ParameterSlot => Boolean(item));
    }
  }

  return [];
}

function mapReportTypeToApi(mode: ReportDeliveryMode): string {
  return mode === "live" ? "live" : "analytical_hub";
}

function mapReportTypeFromApi(value: unknown): ReportDeliveryMode {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "live") return "live";
  return "analytics_hub";
}

function mapStatusToApi(isActive: boolean): string {
  return isActive ? "Active" : "Inactive";
}

function mapStatusFromApi(value: unknown): boolean {
  return String(value ?? "").trim().toLowerCase() !== "inactive";
}

function buildIntentClassification(snapshot: IntentFormSnapshot): IntentClassificationPayload {
  const intent_type = INTENT_TYPE_TO_API[snapshot.classification];

  if (snapshot.classification === "requesting_action") {
    return {
      intent_type,
      orchestrators: snapshot.selectedOrchestrationMappings.slice(0, 1).map((mapping) => {
        const orchestration = snapshot.orchestrations.find((item) => item.id === mapping.orchestrationId);
        return {
          id: mapping.orchestrationId,
          name: orchestration?.name ?? mapping.orchestrationId,
          keywords: normalizeKeywords(mapping.keywords),
          parameters: normalizeParameters(mapping.parameters),
        };
      }),
    };
  }

  if (snapshot.classification === "requesting_report" || snapshot.classification === "live_report") {
    const reportDeliveryMode: ReportDeliveryMode =
      snapshot.classification === "live_report" ? "live" : "analytics_hub";
    const classification: IntentClassificationPayload = {
      intent_type,
      clarification_question: snapshot.askClarifyingQuestion ? snapshot.clarifyingQuestion.trim() : "",
      parameter_prompt: snapshot.parameterPrompt.trim(),
      report_type: mapReportTypeToApi(reportDeliveryMode),
    };

    if (reportDeliveryMode === "live") {
      const selectedId = String(snapshot.selectedDataModel ?? "").trim();
      const instructions = String(snapshot.liveReportInstructions ?? "").trim();
      if (!selectedId) {
        classification.data_models = [];
        classification.additional_context_instructions = instructions;
        return classification;
      }

      const catalogModel = snapshot.dataModels.find((item) => item.id === selectedId);
      // Plain record so the field cannot be dropped by typed object narrowing.
      const liveModelPayload: Record<string, unknown> = {
        id: selectedId,
        name: catalogModel?.name?.trim() || selectedId,
        additional_context_instructions: instructions,
        // camelCase alias for backends that do not use snake_case on nested DTOs
        additionalContextInstructions: instructions,
        keywords: normalizeKeywords(snapshot.liveDataModelMapping?.keywords ?? []),
        parameters: normalizeParameters(snapshot.liveDataModelMapping?.parameters ?? []),
      };
      classification.data_models = [liveModelPayload as unknown as IntentDataModelPayload];
      classification.additional_context_instructions = instructions;
      return classification;
    }

    classification.data_models = snapshot.selectedReportMappings.map((mapping) => {
      const report = snapshot.reportCatalog.find((item) => item.id === mapping.reportId);
      return {
        id: mapping.reportId,
        name: report?.name ?? mapping.reportId,
        additional_context_instructions: "",
        keywords: normalizeKeywords(mapping.keywords),
        parameters: normalizeParameters(mapping.parameters),
      };
    });
    return classification;
  }

  return {
    intent_type,
    knowledge_bases: snapshot.selectedKnowledgeBases.map((id) => {
      const knowledgeBase = snapshot.knowledgeBases.find((item) => item.id === id);
      return {
        id,
        name: knowledgeBase?.name ?? id,
      };
    }),
    keywords: serializeKeywords(snapshot.generalSearchKeywords),
  };
}

export function buildIntentDefinitionPayload(snapshot: IntentFormSnapshot): IntentDefinitionPayload {
  return {
    ...(snapshot.intentId ? { _id: snapshot.intentId } : {}),
    chatbot_id: snapshot.chatbotId,
    name: snapshot.intentName.trim(),
    description: snapshot.description.trim(),
    code: snapshot.intentCode.trim(),
    status: mapStatusToApi(snapshot.isActive),
    confidence_threshold: snapshot.confidenceThreshold,
    intent_classification: buildIntentClassification(snapshot),
    training_phrases: snapshot.trainingPhrases.map((phrase) => phrase.trim()).filter(Boolean),
    fallback: {
      confirm_status_for_low_confidence: snapshot.confirmBelowThreshold,
      low_confidence_value: snapshot.confirmationThreshold / 100,
      confirm_when_multiple_routes_match: snapshot.confirmMultipleRoutes,
      confirm_when_parameters_missing: snapshot.confirmMissingParams,
      fallback_message: snapshot.confirmationMessage.trim(),
    },
  };
}

export function validateIntentForm(snapshot: IntentFormSnapshot): string | null {
  if (!snapshot.chatbotId.trim()) return "Chatbot ID is required.";
  if (!snapshot.intentName.trim()) return "Intent name is required.";
  if (!snapshot.intentCode.trim()) return "Intent code is required.";

  if (snapshot.classification === "requesting_action" && snapshot.selectedOrchestrationMappings.length === 0) {
    return "Select a workflow for action intents.";
  }

  if (snapshot.classification === "requesting_report" || snapshot.classification === "live_report") {
    if (snapshot.classification === "live_report" && !snapshot.selectedDataModel) {
      return "Select a data model for live report intents.";
    }
    if (snapshot.classification === "requesting_report" && snapshot.selectedReportMappings.length === 0) {
      return "Select at least one Analytics Hub report.";
    }
  }

  if (snapshot.classification === "general_search" && snapshot.selectedKnowledgeBases.length === 0) {
    return "Select at least one knowledge base for general search intents.";
  }

  return null;
}

function resolveIntentClassification(intentType: unknown): IntentClassification {
  const key = String(intentType ?? "").trim().toLowerCase();
  return INTENT_TYPE_FROM_API[key] ?? "requesting_report";
}

function parseOrchestratorMappings(
  orchestrators: Array<IntentOrchestratorPayload | Record<string, unknown>> | undefined
): SelectedOrchestrationMapping[] {
  if (!Array.isArray(orchestrators)) return [];
  return orchestrators
    .map((item) => {
      const record = item as Record<string, unknown>;
      return {
        orchestrationId: String(record.id ?? record.Id ?? ""),
        keywords: parseKeywords(record.keywords ?? record.Keywords),
        parameters: parseParameters(record.parameters ?? record.Parameters),
      };
    })
    .filter((item) => item.orchestrationId)
    .slice(0, 1);
}

function parseReportMappings(
  dataModels: Array<IntentDataModelPayload | Record<string, unknown>> | undefined
): SelectedReportMapping[] {
  if (!Array.isArray(dataModels)) return [];
  return dataModels
    .map((item) => {
      const record = item as Record<string, unknown>;
      return {
        reportId: String(record.id ?? record.Id ?? ""),
        keywords: parseKeywords(record.keywords ?? record.Keywords),
        parameters: parseParameters(record.parameters ?? record.Parameters),
      };
    })
    .filter((item) => item.reportId);
}

function extractOrchestrationItems(
  orchestrators: Array<IntentOrchestratorPayload | Record<string, unknown>> | undefined
): OrchestrationItem[] {
  if (!Array.isArray(orchestrators)) return [];
  return orchestrators
    .map((item) => {
      const record = item as Record<string, unknown>;
      const name = String(record.name ?? record.Name ?? "");
      const id = String(record.id ?? record.Id ?? "");
      return {
        id,
        name: name || id,
        description: String(record.description ?? record.Description ?? ""),
        trigger: name || id,
      };
    })
    .filter((item) => item.id);
}

function extractReportItems(
  dataModels: Array<IntentDataModelPayload | Record<string, unknown>> | undefined
): ReportCatalogItem[] {
  if (!Array.isArray(dataModels)) return [];
  return dataModels
    .map((item) => {
      const record = item as Record<string, unknown>;
      const id = String(record.id ?? record.Id ?? "");
      const name = String(record.name ?? record.Name ?? id);
      return {
        id,
        name,
        description: String(record.description ?? record.Description ?? ""),
      };
    })
    .filter((item) => item.id);
}

function extractKnowledgeBaseItems(
  knowledgeBases: Array<IntentKnowledgeBasePayload | Record<string, unknown>> | undefined
): KnowledgeBaseItem[] {
  if (!Array.isArray(knowledgeBases)) return [];
  return knowledgeBases
    .map((item) => {
      const record = item as Record<string, unknown>;
      const id = String(record.id ?? record.Id ?? "");
      const name = String(record.name ?? record.Name ?? id);
      return {
        id,
        name,
        documents: 0,
        scope: name,
      };
    })
    .filter((item) => item.id);
}

function extractDataModelItems(
  dataModels: Array<IntentDataModelPayload | Record<string, unknown>> | undefined
): DataModelItem[] {
  if (!Array.isArray(dataModels)) return [];
  return dataModels
    .map((item) => {
      const record = item as Record<string, unknown>;
      const id = String(record.id ?? record.Id ?? "");
      const name = String(record.name ?? record.Name ?? id);
      return {
        id,
        name,
        entities: parseKeywords(record.keywords ?? record.Keywords),
        source: "Data Model",
      };
    })
    .filter((item) => item.id);
}

export function parseIntentDefinitionRecord(record: IntentDefinitionRecord): Partial<IntentFormSnapshot> {
  return parseIntentDefinitionResponse(record).snapshot;
}

export function parseIntentDefinitionResponse(record: IntentDefinitionRecord): ParsedIntentLoadResult {
  const recordAny = record as IntentDefinitionRecord & Record<string, unknown>;
  const classificationSection = (record.intent_classification ??
    recordAny.intentClassification ??
    {}) as IntentClassificationPayload & Record<string, unknown>;
  const reportDeliveryMode = mapReportTypeFromApi(
    classificationSection.report_type ?? classificationSection.reportType
  );
  const resolvedClassification = resolveIntentClassification(
    classificationSection.intent_type ?? classificationSection.intentType
  );
  const classification: IntentClassification =
    resolvedClassification === "requesting_report" && reportDeliveryMode === "live"
      ? "live_report"
      : resolvedClassification;
  const orchestrators = (classificationSection.orchestrators ??
    classificationSection.Orchestrators) as
    | Array<IntentOrchestratorPayload | Record<string, unknown>>
    | undefined;
  const dataModels = (classificationSection.data_models ??
    classificationSection.dataModels ??
    classificationSection.DataModels) as IntentDataModelPayload[] | undefined;

  const snapshot: Partial<IntentFormSnapshot> = {
    intentId: record._id,
    chatbotId: record.chatbot_id,
    intentName: record.name ?? "",
    intentCode: record.code ?? "",
    description: record.description ?? "",
    isActive: mapStatusFromApi(record.status),
    confidenceThreshold: Number(record.confidence_threshold ?? 0.75),
    classification,
    trainingPhrases: Array.isArray(record.training_phrases) ? record.training_phrases : [],
    confirmBelowThreshold: Boolean(record.fallback?.confirm_status_for_low_confidence),
    confirmationThreshold: Math.round(Number(record.fallback?.low_confidence_value ?? 0.8) * 100),
    confirmMultipleRoutes: Boolean(record.fallback?.confirm_when_multiple_routes_match),
    confirmMissingParams: Boolean(record.fallback?.confirm_when_parameters_missing),
    confirmationMessage: record.fallback?.fallback_message ?? "",
    generalSearchKeywords: parseKeywords(classificationSection.keywords),
  };

  const orchestrationItems = extractOrchestrationItems(orchestrators);
  const reportItems: ReportCatalogItem[] = [];
  const knowledgeBaseItems = extractKnowledgeBaseItems(classificationSection.knowledge_bases);
  const dataModelItems: DataModelItem[] = [];

  if (classification === "requesting_action") {
    snapshot.selectedOrchestrationMappings = parseOrchestratorMappings(orchestrators);
  }

  if (classification === "requesting_report" || classification === "live_report") {
    snapshot.reportDeliveryMode = reportDeliveryMode;
    snapshot.clarifyingQuestion = classificationSection.clarification_question ?? "";
    snapshot.parameterPrompt = classificationSection.parameter_prompt ?? "";
    snapshot.askClarifyingQuestion = Boolean(snapshot.clarifyingQuestion?.trim());

    if (reportDeliveryMode === "live") {
      const liveModel = Array.isArray(dataModels) && dataModels.length > 0
        ? (dataModels[0] as IntentDataModelPayload & Record<string, unknown>)
        : null;
      const liveModelRecord = (liveModel ?? {}) as Record<string, unknown>;

      snapshot.selectedDataModel = String(liveModel?.id ?? liveModelRecord.Id ?? "");
      snapshot.liveReportInstructions = String(
        liveModel?.additional_context_instructions ??
          liveModelRecord.additionalContextInstructions ??
          liveModelRecord.context_instructions ??
          liveModelRecord.ai_context ??
          liveModelRecord.AdditionalContextInstructions ??
          classificationSection.additional_context_instructions ??
          classificationSection.additionalContextInstructions ??
          ""
      );
      snapshot.liveDataModelMapping = {
        keywords: parseKeywords(liveModel?.keywords ?? liveModelRecord.Keywords),
        parameters: parseParameters(
          liveModel?.parameters ?? liveModelRecord.Parameters ?? liveModelRecord.parameter
        ),
      };

      if (Array.isArray(dataModels) && dataModels.length > 0) {
        dataModelItems.push(...extractDataModelItems(dataModels));
      }
    } else if (reportDeliveryMode === "analytics_hub") {
      snapshot.selectedReportMappings = parseReportMappings(dataModels);
      reportItems.push(...extractReportItems(dataModels));
    }
  }

  if (classification === "general_search") {
    snapshot.selectedKnowledgeBases = (classificationSection.knowledge_bases ?? [])
      .map((item) => item.id ?? "")
      .filter(Boolean);
  }

  return {
    snapshot,
    orchestrationItems,
    reportItems,
    knowledgeBaseItems,
    dataModelItems,
  };
}
